// Meta (Facebook) Pixel — loaded only after cookie consent (LSSI-CE art. 22 / GDPR).
//
// The pixel ID comes from VITE_META_PIXEL_ID (baked in at build time). When it is
// unset — dev, test, any environment without the var — this module does nothing,
// so those environments never load the pixel.
//
// Unlike gtag.js (see analytics.ts), the pixel has no consent-mode equivalent: as
// soon as fbevents.js initialises it writes the `_fbp` cookie and sends the
// visitor's IP, user agent and URL to Meta. There is no cookieless mode to fall
// back on, so nothing is requested until the visitor has actually accepted — on
// load for someone who accepted earlier, or on `cookie-consent:accepted` for
// someone accepting now. A visitor who rejects never contacts Meta at all.
//
// The <noscript> tracking image from Meta's stock snippet is deliberately omitted:
// it fires on render with no way to gate it, and a no-JS visitor cannot reach the
// consent banner (its buttons are Stimulus-driven), so it could only ever fire
// without consent.

export {}

const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID

interface Fbq {
  (...args: unknown[]): void
  callMethod?: (...args: unknown[]) => void
  queue: unknown[]
  push: unknown
  loaded: boolean
  version: string
}

declare global {
  interface Window {
    fbq?: Fbq
    _fbq?: Fbq
  }
}

function hasAccepted(): boolean {
  return document.cookie
    .split('; ')
    .some((c) => c === 'cookie_consent=accepted')
}

// Meta's stock snippet, rewritten as readable TypeScript. The queue shim collects
// calls made before fbevents.js finishes loading and replays them on arrival.
function load(): void {
  if (window.fbq) return

  const fbq = function (this: unknown, ...args: unknown[]): void {
    fbq.callMethod ? fbq.callMethod.apply(fbq, args) : fbq.queue.push(args)
  } as Fbq

  fbq.queue = []
  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'

  window.fbq = fbq
  window._fbq ||= fbq

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(script)

  fbq('init', PIXEL_ID)
  fbq('track', 'PageView')
}

if (PIXEL_ID) {
  if (hasAccepted()) {
    load()
  } else {
    document.addEventListener('cookie-consent:accepted', load, { once: true })
  }
}
